import "./js/utils/commonUtils.js";

// @ts-nocheck

//----------- NECESSARY / EARLY MODULES -------------//
import "./js/shared/loadForm.js";
import "./js/shared/tabsContainer.js";

//----------- Core init -------------//
import BarceloComponents from "barcelo-components-init";
import BhgComponents from "bhg-components";
import BaseComponents from "./components.js";
import { BarceloShared } from "barcelo-shared-init";
import { prepareFastbookingHero } from "../../utils/fastbookingHero";

//----------- BARCELO Utilities -------------//
import "./js/general.js";

import "./js/utils/cookieUtils.js";
import "./js/utils/lastjs.js";
import "./js/utils/utag.js";
import "./js/utils/ariaUtils.js";

//----------- Shared JavaScript -------------//
import "./js/shared/currency.js";
import "./js/shared/dataAnchor.js";
import "./js/shared/profile.js";
import "./js/shared/globalEvent.js";

//-------------------------------------------------------------------------------------------------------//

let baseInitStarted = false;
let nonCriticalModulesRequested = false;
let myBarceloStylesRequested = false;

if (typeof bcl !== "undefined" && bcl.s?.dataAnchor?.setPrepareNavigation) {
  bcl.s.dataAnchor.setPrepareNavigation((target, options) => BarceloComponents.prepareForNavigation(target, options));
}

const runWhenIdle = function (callback, timeout = 1500) {
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(callback, { timeout });
    return;
  }

  window.setTimeout(callback, 1);
};

const runAfterLoad = function (callback) {
  if (document.readyState === "complete") {
    callback();
    return;
  }

  window.addEventListener("load", callback, { once: true });
};

const loadNonCriticalModules = function () {
  if (nonCriticalModulesRequested) {
    return;
  }

  nonCriticalModulesRequested = true;

  Promise.allSettled([
    import(/* webpackChunkName: "bcl-bhg-embed" */ "../../components/bhg/accordion"),
    import(/* webpackChunkName: "bcl-bhg-embed" */ "../../components/bhg/tabs"),
    import(/* webpackChunkName: "bcl-shared-recaptcha" */ "./js/shared/recaptchav3.js"),
    import(/* webpackChunkName: "bcl-shared-translate" */ "./js/shared/translate-on-the-fly.js"),
  ]).catch(() => {
    // keep initialization resilient even if one deferred module fails
  });
};

//-------------------------------------------------------------------------------------------------------//

const initSource = function (label, source) {
  return BarceloComponents.init(source, {
    root: document,
    forceImport: false,
    debugLabel: label,
  });
};

//-------------------------------------------------------------------------------------------------------//

const preloadAboveFoldCSS = function () {
  const u = typeof bcl !== "undefined" && (bcl as any).u;
  if (!u) return;

  const hero = document.querySelector(".c-heroslider");
  if (hero && typeof u.loadStyleSheet === "function") {
    prepareFastbookingHero({ hero });
    u.loadStyleSheet("/etc.clientlibs/barcelo/clientlibs/clientlib-heroslider.css", ".c-heroslider.dynamicLoading");

    const firstSlide = hero.querySelector(".swiper-slide");
    if (firstSlide) {
      (firstSlide.querySelectorAll("img") as NodeListOf<HTMLImageElement>).forEach((img) => {
        img.loading = "eager";
        img.fetchPriority = "high";
      });
    }
  }
};

//-------------------------------------------------------------------------------------------------------//

const loadMyBarceloStyles = function () {
  if (myBarceloStylesRequested) {
    return;
  }

  myBarceloStylesRequested = true;

  const u = typeof bcl !== "undefined" && bcl.u;
  if (!u) {
    return;
  }

  const cacheKey = u.getLongCacheKey();

  const href = cacheKey ? `/etc.clientlibs/barcelo/clientlibs/clientlib-mybarcelo.lc-${cacheKey}-lc.min.css` : `/etc.clientlibs/barcelo/clientlibs/clientlib-mybarcelo.min.css`;

  if (document.head.querySelector(`link[href="${href}"]`)) {
    return;
  }

  if (typeof u.loadStyleSheet === "function") {
    u.loadStyleSheet(href);
    return;
  }

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  link.type = "text/css";
  document.head.appendChild(link);
};

const shouldLoadMyBarceloStylesImmediately = function () {
  const htmlTemplate = document.documentElement?.dataset?.template;
  if (htmlTemplate && htmlTemplate.toLowerCase() === "mybarcelo") {
    return true;
  }

  const myBarceloPageSelector = [".mybarcelo-JS", "[data-mybarcelo]", ".myb-user-entry", ".c-myb-hero", ".c-myb-details", ".c-myb-features-2", ".c-myb-preferences", ".myb-loyalty-hero", "#home_mybarcelo"].join(", ");

  return Boolean(document.querySelector(myBarceloPageSelector));
};

const bindMyBarceloStylesOnInteraction = function () {
  const interactionSelector = ".js-item-myBarcelo, .js-name-myb-parent, .js-item-myBarcelo-popover";

  if (!document.querySelector(interactionSelector)) {
    return;
  }

  const loadOnInteraction = function (event) {
    if (!event.target.closest(interactionSelector)) {
      return;
    }

    document.removeEventListener("pointerover", loadOnInteraction, true);
    document.removeEventListener("focusin", loadOnInteraction, true);
    document.removeEventListener("click", loadOnInteraction, true);
    loadMyBarceloStyles();
  };

  document.addEventListener("pointerover", loadOnInteraction, true);
  document.addEventListener("focusin", loadOnInteraction, true);
  document.addEventListener("click", loadOnInteraction, true);
};

const initializeComponents = async function () {
  if (baseInitStarted) {
    return;
  }

  baseInitStarted = true;

  try {
    preloadAboveFoldCSS();

    if (BarceloShared && typeof BarceloShared.init === "function") {
      BarceloShared.init();
    }

    await Promise.all([initSource("BHG", BhgComponents), initSource("BASE", BaseComponents)]);

    document.dispatchEvent(new CustomEvent("bcl-base-components-loaded"));

    if (shouldLoadMyBarceloStylesImmediately()) {
      loadMyBarceloStyles();
    } else {
      bindMyBarceloStylesOnInteraction();
    }

    if (typeof bcl !== "undefined" && bcl.u && typeof bcl.u.preInit === "function") {
      bcl.u.preInit();
    }

    runAfterLoad(() => runWhenIdle(loadNonCriticalModules, 2200));
  } catch (error) {
    console.error("Init error:", error);
  }
};

//-------------------------------------------------------------------------------------------------------//

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeComponents, { once: true });
} else {
  initializeComponents();
}
